import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Table from '../../shared/components/layout/Table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLandById,
  uploadFilesToS3,
  updateLandAdditionalDocs,
  getLandAdditionalDocs,
  shareAdditionalDocuments
} from '../services/api/implementation/impl';
import { useParams, useNavigate } from 'react-router-dom';
import { BsFiletypePdf } from 'react-icons/bs';
import { AiOutlineFileImage } from 'react-icons/ai';
import { FaRegFile } from 'react-icons/fa';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import { MdShare } from 'react-icons/md';
import { IoMdClose } from 'react-icons/io';
import { FiUpload } from 'react-icons/fi';
import toast from 'react-hot-toast';
import Loader from '../../shared/components/ui/Loader';

/**
 * Parses area type strings like "sq/acres", "sq/hectares", "sq/gunta"
 * and normalizes them to "acre", "hectare", or "gunta".
 */
const parseAreaType = (areaType) => {
  const userId = Cookies.get('userId');
  if (!areaType) return '';
  let t = areaType.toLowerCase().trim();

  // Remove sq/
  t = t.replace('sq/', '');

  // Normalize "acres" to "acre"
  if (t.includes('acres')) t = t.replace('acres', 'acre');
  // Normalize "hectares" to "hectare"
  if (t.includes('hectares')) t = t.replace('hectares', 'hectare');
  // Normalize "guntha" to "gunta"
  if (t.includes('guntha')) t = t.replace('guntha', 'gunta');
  // Normalize "cent" or "cents" to "cents"
  if (t === 'cent' || t === 'cents') t = 'cents';
  // Normalize "katha"
  if (t.includes('katha')) t = 'katha';

  return t;
};

// Helper function to check if state uses Survey terminology
const isSurveyState = (state) => {
  const surveyStates = [
    'andhra pradesh',
    'telangana',
    'karnataka',
    'kerala',
    'tamil nadu',
    'gujarat'
  ];
  return surveyStates.includes(state?.toLowerCase());
};

// Helper function to check if state uses Dag terminology
const isDagState = (state) => {
  return state?.toLowerCase() === 'west bengal';
};

const getLandLabel = (state) => {
  if (isDagState(state)) return 'Dag';
  if (isSurveyState(state)) return 'Survey';
  return 'Khasra';
};

const formatAreaType = (areaType) => {
  return areaType?.replace('sq/', '');
};
/**
 * Converts a value in either "hectare", "acre", or "gunta"
 * into square meters. Returns null if value is falsy or type is unrecognized.
 */
const convertToSqMt = (value, type) => {
  if (!value || !type) return null;
  const val = parseFloat(value);
  if (Number.isNaN(val)) return null;

  switch (type.toLowerCase()) {
    case 'hectare':
      // 1 hectare = 10,000 sq. meters
      return (val * 10000).toFixed(2);
    case 'acres':
      // 1 acre ≈ 4,046.85642 sq. meters
      return (val * 4046.85642).toFixed(2);
    case 'gunta':
      // 1 gunta ≈ 101.17 sq. meters (approx)
      return (val * 101.17).toFixed(2);
    case 'cents':
      // 1 cent ≈ 40.47 sq. meters (1 acre = 4046.86 sq.m / 100 cents)
      return (val * 40.47).toFixed(2);
    case 'katha':
      // 1 Katha ≈ 66.89 sq meters (using 1 sq ft = 0.092903 sq m, and 1 Katha = 720 sq ft)
      return (val * 66.89).toFixed(2);
    default:
      return '-';
  }
};

/**
 * Converts a value in either "hectare", "acre", or "gunta"
 * into square feet. Returns null if value is falsy or type is unrecognized.
 */
const convertToSqFt = (value, type) => {
  if (!value || !type) return null;
  const val = parseFloat(value);
  if (Number.isNaN(val)) return null;

  switch (type.toLowerCase()) {
    case 'hectare':
      // 1 hectare ≈ 107,639.104 sq. feet
      return (val * 107639.104).toFixed(2);
    case 'acres':
      // 1 acre = 43,560 sq. feet
      return (val * 43560).toFixed(2);
    case 'gunta':
      // 1 gunta ≈ 1,089 sq. feet (approx)
      return (val * 1089).toFixed(2);
    case 'cents':
      // 1 cent ≈ 435.6 sq. feet (1 acre = 43,560 sq.ft / 100 cents)
      return (val * 435.6).toFixed(2);
    case 'katha':
      // 1 Katha = 720 sq ft (common standard)
      return (val * 720).toFixed(2);
    default:
      return '-';
  }
};

/*
  Helper Functions for Documents
*/
const getFileIcon = (fileName) => {
  const extension = fileName?.split('.')?.pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return <BsFiletypePdf size={80} />;
    case 'png':
    case 'jpg':
    case 'jpeg':
      return <AiOutlineFileImage size={90} />;
    default:
      return <FaRegFile size={80} />; // Default icon
  }
};

const truncateFileName = (fileName, maxLength = 15) => {
  if (!fileName) return 'Unnamed File';
  if (fileName.length <= maxLength) return fileName;
  const extension = fileName.split('.').pop();
  const nameWithoutExtension = fileName.slice(0, -(extension.length + 1));
  return `${nameWithoutExtension.slice(0, maxLength - 3)}...${extension}`;
};

// Document sharing helper functions
const toggleDocumentSelection = (document, selectedForSharing, setSelectedForSharing) => {
  const isSelected = selectedForSharing.some((doc) => doc.url === document.url);
  if (isSelected) {
    setSelectedForSharing((prev) => prev.filter((doc) => doc.url !== document.url));
  } else {
    setSelectedForSharing((prev) => [...prev, document]);
  }
};

const isDocumentSelected = (document, selectedForSharing) => {
  return selectedForSharing.some((doc) => doc.url === document.url);
};

const clearSelection = (setSelectedForSharing) => {
  setSelectedForSharing([]);
};

const handleShareClick = (document, setSelectedDocuments, setEmailBody, setShowShareModal) => {
  setSelectedDocuments([document]);
  setEmailBody(`Please find the document below:\n\n${document.name}\n${document.url}`);
  setShowShareModal(true);
};

const handleBulkShare = (
  selectedForSharing,
  setSelectedDocuments,
  setEmailBody,
  setShowShareModal
) => {
  if (selectedForSharing.length === 0) {
    toast.error('Please select at least one document to share');
    return;
  }
  setSelectedDocuments(selectedForSharing);
  setEmailBody(
    `Please find the documents below:\n\n${selectedForSharing.map((doc) => `${doc.name}\n${doc.url}`).join('\n\n')}`
  );
  setShowShareModal(true);
};

const handleSendEmail = async (
  emailRecipients,
  emailBody,
  selectedDocuments,
  setShowShareModal,
  setSelectedDocuments,
  setEmailRecipients,
  setEmailBody,
  setSelectedForSharing,
  handleSendEmailMutation
) => {
  try {
    // Validate required fields
    if (!emailRecipients.trim()) {
      toast.error('Please enter email recipients');
      return;
    }

    // Parse email recipients (comma or space separated)
    const emails = emailRecipients
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter((email) => email);

    // Extract document URLs
    const docs = selectedDocuments.map((doc) => doc.url);

    const payload = {
      emails,
      docs,
      body: emailBody || ''
    };

    await shareAdditionalDocuments(payload);

    toast.success('Documents shared successfully!');
    setShowShareModal(false);

    // Reset form and clear selections
    setSelectedDocuments([]);
    setSelectedForSharing([]);
    setEmailRecipients('');
    setEmailBody('');
  } catch (error) {
    console.error('Error sharing documents:', error);
    toast.error(error?.message || error?.response?.data?.message || 'Failed to share documents');
  }
};

const handleCopyToClipboard = (emailBody) => {
  try {
    // Copy only the email body
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(emailBody)
        .then(() => {
          toast.success('Email body copied to clipboard!');
        })
        .catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = emailBody;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          toast.success('Email body copied to clipboard!');
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = emailBody;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Email body copied to clipboard!');
    }
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    toast.error('Error copying to clipboard. Please try again.');
  }
};

const closeShareModal = (
  setShowShareModal,
  setSelectedDocuments,
  setEmailRecipients,
  setEmailBody,
  setSelectedForSharing
) => {
  setShowShareModal(false);
  setSelectedDocuments([]);
  setEmailRecipients('');
  setEmailBody('');
  setSelectedForSharing([]);
};

/*
  Document Renderer Component
*/
const DocumentRenderer = ({
  title,
  documents,
  selectedForSharing,
  setSelectedForSharing,
  setSelectedDocuments,
  setEmailBody,
  setShowShareModal,
  openUploadModal
}) => {
  if (!documents || (Array.isArray(documents) && documents.length === 0)) return null;

  // Normalize documents to an array of { name, url }
  let normalizedDocs = [];

  if (Array.isArray(documents)) {
    normalizedDocs = documents.flatMap((doc) => {
      if (typeof doc === 'string') {
        // If doc is just a URL
        return [{ name: doc.split('/').pop(), url: doc }];
      } else if (doc?.name && doc?.url) {
        return [{ name: doc.name, url: doc.url }];
      } else if (doc?.url) {
        return [{ name: doc.url.split('/').pop(), url: doc.url }];
      }
      return [];
    });
  } else if (typeof documents === 'object' && documents !== null) {
    // Single object
    if (documents.name && documents.url) {
      normalizedDocs.push({ name: documents.name, url: documents.url });
    } else if (documents.url) {
      normalizedDocs.push({ name: documents.url.split('/').pop(), url: documents.url });
    }
  }

  if (normalizedDocs.length === 0) return null;

  return (
    <div className="flex flex-col gap-10 border rounded-md border-gray-200 p-3">
      <h3 className="border w-full text-center font-semibold p-2 rounded-lg bg-slate-600 text-lg text-white">
        {title}
      </h3>
      <div className="flex flex-wrap gap-3">
        {normalizedDocs.map((file, i) => (
          <div
            key={i}
            className="relative flex flex-col border w-[12rem] p-5 rounded-xl justify-center items-center group">
            {/* Checkbox for multi-selection */}
            <input
              type="checkbox"
              checked={isDocumentSelected(file, selectedForSharing)}
              onChange={() =>
                toggleDocumentSelection(file, selectedForSharing, setSelectedForSharing)
              }
              className="absolute top-2 left-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />

            <a href={file.url} target="_blank" rel="noreferrer" download>
              {getFileIcon(file.name)}
            </a>
            <p className="text-center">{truncateFileName(file.name)}</p>

            {/* Share Icon */}
            <button
              onClick={() =>
                handleShareClick(file, setSelectedDocuments, setEmailBody, setShowShareModal)
              }
              className="absolute top-2 right-2 bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600 transition-colors duration-200"
              title="Share this document">
              <MdShare size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const LandDetails = () => {
  const navigate = useNavigate();
  const { id: landId } = useParams();
  const userId = Cookies.get('userId');

  const queryClient = useQueryClient();

  const {
    data,
    refetch: refetchLand,
    isLoading: isLandLoading
  } = useQuery({
    queryKey: ['land', landId],
    queryFn: () => getLandById(landId)
  });

  const {
    data: landAdditionalDocs,
    refetch: refetchAdditionalDocs,
    isFetching: isAdditionalDocsFetching
  } = useQuery({
    queryKey: ['landAdditionalDocs', landId],
    queryFn: () => getLandAdditionalDocs(landId)
  });

  /*
    Effects
  */

  // Track page view
  useEffect(() => {
    mixpanel.track('Land Details Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchLand();
  }, [refetchLand]);

  /*
    State
  */
  const [selectedFilter, setSelectedFilter] = useState('Land Details');
  const handleButtonClick = (filter) => {
    setSelectedFilter(filter);
  };
  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);

  // Document sharing state
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [selectedForSharing, setSelectedForSharing] = useState([]);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailBody, setEmailBody] = useState('');

  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileNames, setFileNames] = useState({}); // Store custom names for each file
  const [isUploading, setIsUploading] = useState(false);

  // Upload functionality
  const handleFileSelect = (event) => {
    const newFiles = Array.from(event.target.files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    // Clear the input so the same files can be selected again if needed
    event.target.value = '';
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    // Also remove the custom name for this file
    setFileNames((prev) => {
      const newNames = { ...prev };
      delete newNames[index];
      return newNames;
    });
  };

  const handleFileNameChange = (index, customName) => {
    setFileNames((prev) => ({
      ...prev,
      [index]: customName
    }));
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setSelectedFiles([]);
    setFileNames({});
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setSelectedFiles([]);
    setFileNames({});
  };

  const { mutate: handleUploadFiles } = useMutation({
    mutationFn: async ({ files, customNames }) => {
      // Step 1: Upload files to S3 first
      const uploadedFiles = await uploadFilesToS3(files);

      // Step 2: Create the payload with custom names and S3 URLs
      const docs = uploadedFiles.map((file, index) => {
        // Get the file extension from the original file
        const originalFileName = files[index].name;
        const fileExtension = originalFileName.substring(originalFileName.lastIndexOf('.'));

        // Append extension to custom name
        const nameWithExtension = customNames[index]
          ? `${customNames[index]}${fileExtension}`
          : file.name;

        return {
          name: nameWithExtension,
          url: file.url
        };
      });

      const payload = {
        land_id: parseInt(landId),
        docs: docs
      };

      // Step 3: Send the docs with URLs to the land API
      return await updateLandAdditionalDocs(payload);
    },
    onSuccess: () => {
      toast.success('Files uploaded successfully!');
      setShowUploadModal(false);
      setSelectedFiles([]);
      setFileNames({});
      refetchAdditionalDocs(); // Refetch additional docs
      queryClient.invalidateQueries(['landAdditionalDocs', landId]);
      setIsUploading(false);
    },
    onError: (error) => {
      console.error('Upload error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to upload files');
      setIsUploading(false);
    }
  });

  const handleUploadSubmit = () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    // Validate that all files have custom names
    const missingNames = selectedFiles.some((file, index) => !fileNames[index]?.trim());
    if (missingNames) {
      toast.error('Please provide a name for all documents');
      return;
    }

    setIsUploading(true);
    handleUploadFiles({ files: selectedFiles, customNames: fileNames });
  };

  /*
    For the table:
    We create columns to display:
      1) Khasra/Survey Number
      2) Main area (Khasra Area)
      3) Guntha/Cents / decimal (the sub-area)
      4) Main area in SqMt
      5) Main area in SqFt
      6) Guntha/Cents / decimal in SqMt
      7) Guntha/Cents / decimal in SqFt
      8) Owner (clickable name)
  */

  // Get the sub-area type from the first khasra to determine header label (Guntha or Cents / decimal)
  const firstKhasraSubAreaType =
    data?.ownerLandMappings?.[0]?.owner?.ownerKhasraMappings?.[0]?.khasra?.subAreaType;
  const parsedSubAreaType = parseAreaType(firstKhasraSubAreaType);
  const subAreaLabel = parsedSubAreaType === 'cents' ? 'Cents / decimal' : 'Guntha';

  const khasraHeaders = [
    `${getLandLabel(data?.state)} Number `,
    `${getLandLabel(data?.state)} Area (${formatAreaType(data?.total_mainAreaType)})`,
    subAreaLabel,
    'Area in Sq Mt',
    'Area in Sq Ft',
    `${subAreaLabel}\nin Sq Mt`,
    `${subAreaLabel}\nin Sq Ft`,
    'Owner'
  ];

  // Build combined rows with owner column and clickable owner name
  const khasraRows = data?.ownerLandMappings
    ?.flatMap((mapping) => {
      const owner = mapping?.owner;
      if (Array.isArray(mapping?.owner?.ownerKhasraMappings)) {
        return mapping.owner.ownerKhasraMappings.flatMap((khasraMapping, idx) => {
          const item = khasraMapping?.khasra;
          const formatedmainLandType = formatAreaType(data?.total_mainAreaType);
          const area_sqmt = convertToSqMt(item?.mainArea, formatedmainLandType);
          const area_sqft = convertToSqFt(item?.mainArea, formatedmainLandType);
          const subAreaType = parseAreaType(item?.subAreaType);
          const guntha_sqmt = convertToSqMt(item?.subArea, subAreaType);
          const guntha_sqft = convertToSqFt(item?.subArea, subAreaType);

          const ownerCell = (
            <span
              className="text-blue-600 underline cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOwner(owner);
                setIsOwnerModalOpen(true);
              }}>
              {owner?.name || '-'}
            </span>
          );

          // Ensure key order matches headers order and include identifier at end
          return [
            {
              number: item?.number,
              area: item?.mainArea || 0,
              guntha: item?.subArea || 0,
              area_sqmt,
              area_sqft,
              guntha_sqmt,
              guntha_sqft,
              owner: ownerCell,
              id: `${owner?.id || 'o'}-${item?.id || `${idx}`}`
            }
          ];
        });
      }
      return [];
    })
    .filter(Boolean);

  /*
    Document sections to display
  */
  const allKhasraMaps = data?.ownerLandMappings
    ?.flatMap((m) => m?.owner?.ownerKhasraMappings || [])
    ?.flatMap((km) => km?.khasra?.khasra_map_link || [])
    ?.filter(Boolean);

  const allKhasraP1B2 = data?.ownerLandMappings
    ?.flatMap((m) => m?.owner?.ownerKhasraMappings || [])
    ?.flatMap((km) => km?.khasra?.khasra_p1b2_link || [])
    ?.filter(Boolean);

  const allLandUseMaps = data?.ownerLandMappings
    ?.flatMap((m) => m?.owner?.ownerKhasraMappings || [])
    ?.flatMap((km) => km?.khasra?.land_use_map || [])
    ?.filter(Boolean);

  const landDocuments = [
    { title: 'Registry Documents', documents: data?.registryDocs || data?.registry_doc_link },
    { title: 'Advocate Searches', documents: data?.advocateSearches || data?.advocate_search_link },
    {
      title: 'Diversion Orders',
      documents: data?.diversionOrders || data?.diversion_order_link || data?.diversionOrderFiles
    },
    {
      title: 'Khasra Map Links',
      documents: allKhasraMaps && allKhasraMaps.length > 0 ? allKhasraMaps : data?.khasra_map_link
    },
    {
      title: 'Khasra P1B2 Links',
      documents: allKhasraP1B2 && allKhasraP1B2.length > 0 ? allKhasraP1B2 : data?.khasra_p1b2_link
    },
    {
      title: 'Land Use Maps',
      documents: allLandUseMaps && allLandUseMaps.length > 0 ? allLandUseMaps : data?.land_use_map
    },
    {
      title: 'Google Location Images',
      documents: data?.google_map_image_link
    },
    {
      title: 'Google Maps',
      documents: data?.google_map_link ? [{ name: 'Google Map', url: data.google_map_link }] : null
    }
  ];

  const { collapsed } = useSidebar();

  return (
    <Layout>
      {/* Header */}
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
        <Header data={{ name: `Lands > ${data?.name || 'Loading...'}` }} backArrow />
      </div>

      {/* Filter Buttons */}
      <div
        className={`flex z-5 items-center justify-between px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100 shadow-sm transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
        <div className="flex w-full justify-between items-center gap-5 cursor-pointer">
          <span className="font-semibold">{selectedFilter || 'Land Details'}</span>
          <div className="button-group flex rounded-lg">
            <div
              className={`border border-x-1 px-2 py-1 font-helvetica cursor-pointer button ${selectedFilter === 'Land Details' ? `bg-customBlue text-white` : 'text-textC'
                } rounded-tl rounded-bl`}
              onClick={() => handleButtonClick('Land Details')}>
              Land Details
            </div>
            <div
              className={`border border-x-1 px-2 py-1 font-helvetica cursor-pointer button ${selectedFilter === 'Documents' ? `bg-customBlue text-white` : 'text-textC'
                }`}
              onClick={() => handleButtonClick('Documents')}>
              Documents
            </div>
          </div>
        </div>
      </div>

      {/* Content */}

      {selectedFilter === 'Land Details' ? (
        <div className="w-full flex flex-col absolute top-[10rem] z-5 pl-5 pr-5 bg-gray-100 pb-10 overflow-auto">
          <Loader isVisible={isLandLoading} />
          {/* Combined Khasra Details with Owner column */}
          <div>
            <h3 className="m-3 text-lg font-semibold">
              {getLandLabel(data?.state)} Details
            </h3>
            <div className="h-[20rem] overflow-auto hide-scrollbar Editlevel-table-container">
              <style>{`
                  .Editlevel-table-container .table-body {
                    position: static !important;
                    overflow-y: visible !important;
                    max-height: none !important;  
                    height: auto !important;
                    top: auto !important;
                    padding-bottom: 0 !important;
                  }
                  .Editlevel-table-container .table-container {
                    height: auto !important;
                  }
                  .Editlevel-table-container > div {
                    height: auto !important;  
                  }
                `}</style>
              <Table headers={khasraHeaders} identifier={'id'} data={khasraRows} isWrap={true} />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full flex flex-col absolute top-[10rem] z-5 pl-5 pr-5 bg-gray-100 pb-10 overflow-auto">
          <Loader isVisible={isLandLoading || isUploading || isAdditionalDocsFetching} />
          {/* Bulk Share Controls */}
          {selectedForSharing.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-blue-800 font-medium">
                    {selectedForSharing.length} document{selectedForSharing.length > 1 ? 's' : ''}{' '}
                    selected
                  </span>
                  <button
                    onClick={() => clearSelection(setSelectedForSharing)}
                    className="text-blue-600 hover:text-blue-800 text-sm underline">
                    Clear Selection
                  </button>
                </div>
                <Button
                  onClick={() =>
                    handleBulkShare(
                      selectedForSharing,
                      setSelectedDocuments,
                      setEmailBody,
                      setShowShareModal
                    )
                  }
                  className="bg-blue-600 hover:bg-blue-700 text-white">
                  Share Selected ({selectedForSharing.length})
                </Button>
              </div>
            </div>
          )}

          {/* Documents Section */}
          <div className="flex flex-wrap gap-10">
            {landDocuments.map((docSection, index) => (
              <DocumentRenderer
                key={index}
                title={docSection.title}
                documents={docSection.documents}
                selectedForSharing={selectedForSharing}
                setSelectedForSharing={setSelectedForSharing}
                setSelectedDocuments={setSelectedDocuments}
                setEmailBody={setEmailBody}
                setShowShareModal={setShowShareModal}
                openUploadModal={openUploadModal}
              />
            ))}
          </div>

          {/* Dedicated Additional Documents Section */}
          <div className="flex flex-col gap-10 border-t pt-5 border-gray-300 mt-3">
            <div className="flex items-center justify-between">
              <h3 className="border text-center font-semibold p-2 rounded-lg bg-slate-600 text-lg text-white whitespace-nowrap">
                Additional Documents
              </h3>
              <button
                onClick={openUploadModal}
                type="button"
                className="border border-customBlue bg-customBlue text-white py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline flex items-center gap-2">
                <FiUpload size={16} />
                Upload Files
              </button>
            </div>
            <div className="flex gap-3 flex-wrap">
              {landAdditionalDocs?.map((item, i) => (
                <div
                  key={i}
                  className="relative flex flex-col border w-[12rem] p-5 rounded-xl justify-center items-center group">
                  {/* Checkbox for multi-selection */}
                  <input
                    type="checkbox"
                    checked={isDocumentSelected(item, selectedForSharing)}
                    onChange={() =>
                      toggleDocumentSelection(item, selectedForSharing, setSelectedForSharing)
                    }
                    className="absolute top-2 left-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />

                  <a href={item.url} target="_blank" rel="noreferrer" download>
                    {getFileIcon(item.name)}
                  </a>
                  <p className="text-center">{truncateFileName(item.name)}</p>

                  {/* Share Icon */}
                  <button
                    onClick={() =>
                      handleShareClick(item, setSelectedDocuments, setEmailBody, setShowShareModal)
                    }
                    className="absolute top-2 right-2 bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600 transition-colors duration-200"
                    title="Share this document">
                    <MdShare size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isOwnerModalOpen && (
        <Modal name="Owner Details" width={40} onClose={() => setIsOwnerModalOpen(false)}>
          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <span className="w-32 text-gray-600">Name</span>
              <span className="font-medium">{selectedOwner?.name || '-'}</span>
            </div>
            <div className="flex gap-2">
              <span className="w-32 text-gray-600">Email</span>
              <span className="font-medium">{selectedOwner?.email || '-'}</span>
            </div>
            <div className="flex gap-2">
              <span className="w-32 text-gray-600">Phone</span>
              <span className="font-medium">{selectedOwner?.phone_number || '-'}</span>
            </div>
            <div className="flex gap-2">
              <span className="w-32 text-gray-600">Aadhar</span>
              <span className="font-medium">{selectedOwner?.aadhar || '-'}</span>
            </div>
            <div className="flex gap-2">
              <span className="w-32 text-gray-600">PAN</span>
              <span className="font-medium">{selectedOwner?.pan_number || '-'}</span>
            </div>
          </div>
        </Modal>
      )}

      {/* Share Document Modal */}
      {showShareModal && (
        <Modal
          isDirty
          confirmOnClose
          width={50}
          name="Share Document"
          onClose={() =>
            closeShareModal(
              setShowShareModal,
              setSelectedDocuments,
              setEmailRecipients,
              setEmailBody,
              setSelectedForSharing
            )
          }>
          <div className="flex flex-col gap-4">
            {selectedDocuments.length > 0 && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Selected {selectedDocuments.length === 1 ? 'document' : 'documents'}:
                </p>
                <div className="space-y-1">
                  {selectedDocuments.map((doc, index) => (
                    <p key={index} className="text-sm text-gray-600">
                      • {doc.name}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Recipients *
              </label>
              <input
                type="email"
                placeholder="Enter email addresses (comma-separated)"
                value={emailRecipients}
                onChange={(e) => setEmailRecipients(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Body</label>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={6}
                placeholder="Email body content..."
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                onClick={() =>
                  closeShareModal(
                    setShowShareModal,
                    setSelectedDocuments,
                    setEmailRecipients,
                    setEmailBody,
                    setSelectedForSharing
                  )
                }
                displayType="secondary">
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => handleCopyToClipboard(emailBody)}
                displayType="secondary">
                Copy to Clipboard
              </Button>
              <Button
                type="button"
                onClick={() =>
                  handleSendEmail(
                    emailRecipients,
                    emailBody,
                    selectedDocuments,
                    setShowShareModal,
                    setSelectedDocuments,
                    setEmailRecipients,
                    setEmailBody,
                    setSelectedForSharing
                  )
                }
                disabled={!emailRecipients}>
                Send Email
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <Modal
          isDirty
          confirmOnClose
          width={50}
          name="Upload Additional Documents"
          onClose={closeUploadModal}>
          <div className="flex flex-col gap-4 relative max-h-[48rem]">
            {/* File Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Files (Multiple files allowed)
              </label>
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.xlsx,.xls"
              />
              <p className="text-xs text-gray-500 mt-1">
                Supported formats: PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, TXT, XLSX, XLS
              </p>
            </div>

            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-gray-700">
                    Selected files ({selectedFiles.length}):
                  </p>
                  <button
                    onClick={() => setSelectedFiles([])}
                    className="text-red-600 hover:text-red-800 text-sm underline">
                    Clear All
                  </button>
                </div>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 bg-white p-3 rounded border">
                      <div className="flex-shrink-0">{getFileIcon(file.name)}</div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">
                          Original file:{' '}
                          <span className="font-medium text-gray-700">{file.name}</span>
                        </p>
                        <input
                          type="text"
                          placeholder="Enter custom document name *"
                          value={fileNames[index] || ''}
                          onChange={(e) => handleFileNameChange(index, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                          required
                        />
                      </div>
                      <button
                        onClick={() => removeSelectedFile(index)}
                        className="text-red-500 hover:text-red-700 flex-shrink-0">
                        <IoMdClose size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" onClick={closeUploadModal} displayType="secondary">
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleUploadSubmit}
                disabled={selectedFiles.length === 0 || isUploading}
                className="bg-green-600 hover:bg-green-700 text-white">
                {isUploading
                  ? 'Uploading...'
                  : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default LandDetails;
